/* Display-only labels for the existing Zepto controls. No pricing, preview,
 * focus, viewport or scrolling handlers. Custom-element lifecycle scopes and
 * cleans up each product instance, including Shopify section replacements. */
(() => {
  'use strict';
  if (customElements.get('bw-zepto-controls')) return;
  const appSelector = '.product-personalizer';
  const groupSelector = '.pplr-color-select';
  const ownedSelector = '.bw-zepto-size-label';
  const relevantNode = node => node.nodeType === 1 && !node.matches(ownedSelector) &&
    !node.closest(ownedSelector);

  class ZeptoControls extends HTMLElement {
    connectedCallback() {
      if (this.root) return;
      this.root = this.closest('product-info[id^="MainProduct-"]');
      if (!this.root) return;
      this.captions = new Map();
      this.sizeLabels = new Set();
      this.pending = false;
      this.onReady = () => this.schedule();
      this.onControl = event => {
        if (this.app?.contains(event.target)) this.schedule();
      };
      this.onUnload = event => { if (event.target.contains(this)) this.dispose(); };
      this.appObserver = new MutationObserver(records => {
        if (records.some(record => record.type === 'attributes' ||
          [...record.addedNodes, ...record.removedNodes].some(relevantNode))) this.schedule();
      });
      this.structureObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches(appSelector) || node.querySelector(appSelector))))) this.schedule();
      });
      this.structureObserver.observe(this.root, { childList: true, subtree: true });
      ['click', 'input', 'change'].forEach(type => this.root.addEventListener(type, this.onControl));
      ['pplrAppInitiated', 'pplrAppBuild', 'pageshow'].forEach(type => window.addEventListener(type, this.onReady));
      document.addEventListener('shopify:section:unload', this.onUnload);
      this.sync();
    }
    disconnectedCallback() { this.dispose(); }
    schedule() {
      if (this.pending || !this.root) return;
      this.pending = true;
      queueMicrotask(() => { this.pending = false; if (this.root) this.sync(); });
    }
    selectedName(group) {
      // Prefer the app's accessible selection state. Class selection supports
      // older Zepto markup; names always come from the actual selected option.
      const checked = group.querySelector('.pplr-swatch-element[aria-checked="true"],input[type="radio"]:checked');
      const hasAriaSelection = group.querySelector('.pplr-swatch-element[aria-checked]');
      const selected = checked || (!hasAriaSelection && group.querySelector('.pplr-swatch-element.selected'));
      if (selected) return (selected.getAttribute('data-name') || selected.getAttribute('aria-label') || '').trim();
      const select = group.querySelector('select.pplr_select');
      const option = select?.selectedOptions?.[0];
      return select?.value && select.value !== 'blank' && option ?
        (option.getAttribute('data-name') || option.getAttribute('aria-label') || option.textContent).trim() : '';
    }
    restoreCaption(label, original) {
      if (original === null) label.removeAttribute('data-bw-thread-caption');
      else label.setAttribute('data-bw-thread-caption', original);
    }
    detachApp() {
      this.appObserver.disconnect();
      this.captions.forEach((original, label) => this.restoreCaption(label, original));
      this.captions.clear();
      this.sizeLabels.forEach(label => label.remove());
      this.sizeLabels.clear();
      if (this.app && !this.hadClass) this.app.classList.remove('bw-controls-ready');
      this.app = null;
    }
    sync() {
      const next = [...this.root.querySelectorAll(appSelector)].find(app =>
        !app.closest('.pplr_crop-modal,.pplr_print_main')) || null;
      if (next !== this.app) {
        this.detachApp();
        this.app = next;
        if (next) {
          this.hadClass = next.classList.contains('bw-controls-ready');
          this.appObserver.observe(next, { childList: true, subtree: true, attributes: true,
            attributeFilter: ['class', 'aria-checked', 'checked', 'selected', 'value', 'data-name', 'aria-label'] });
        }
      }
      if (!this.app) return;
      const colourGroups = [...this.app.querySelectorAll(groupSelector)];
      const sizeRows = [...this.app.querySelectorAll('.pplr-text .textBiggerSmaller')];
      if (!colourGroups.length && !sizeRows.length) return;
      if (!this.app.classList.contains('bw-controls-ready')) this.app.classList.add('bw-controls-ready');
      const labels = new Set();
      colourGroups.forEach(group => {
        const label = group.querySelector(':scope > .pplrlabel');
        if (!label) return;
        labels.add(label);
        if (!this.captions.has(label)) this.captions.set(label, label.getAttribute('data-bw-thread-caption'));
        const name = this.selectedName(group);
        const caption = name ? `Gekozen garenkleur: ${name}` : 'Kies je garenkleur';
        if (label.getAttribute('data-bw-thread-caption') !== caption) label.setAttribute('data-bw-thread-caption', caption);
      });
      this.captions.forEach((original, label) => {
        if (!labels.has(label)) { this.restoreCaption(label, original); this.captions.delete(label); }
      });
      sizeRows.forEach(row => {
        let label = row.querySelector(':scope > .bw-zepto-size-label');
        if (!label) {
          label = document.createElement('span');
          label.className = 'bw-zepto-size-label';
          label.textContent = 'Lettergrootte';
          row.prepend(label);
          this.sizeLabels.add(label);
        }
      });
      this.sizeLabels.forEach(label => {
        if (!this.app.contains(label)) this.sizeLabels.delete(label);
      });
    }
    dispose() {
      if (!this.root) return;
      this.structureObserver.disconnect();
      this.detachApp();
      ['click', 'input', 'change'].forEach(type => this.root.removeEventListener(type, this.onControl));
      ['pplrAppInitiated', 'pplrAppBuild', 'pageshow'].forEach(type => window.removeEventListener(type, this.onReady));
      document.removeEventListener('shopify:section:unload', this.onUnload);
      this.root = null;
    }
  }
  customElements.define('bw-zepto-controls', ZeptoControls);
})();
